import React, { useState, useEffect, useCallback } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import './StripePaymentModal.css';
import { createPaymentIntent } from '../functions/topup';

// Component for the actual payment form inside Elements provider
const CheckoutForm = ({ amount, onSuccess, onClose }: { amount: number, onSuccess: (id: string) => void, onClose: () => void }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [processing, setProcessing] = useState(false);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setProcessing(true);
        setErrorMessage(null);

        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                // Using current page as return_url, but 'if_required' usually avoids redirect for cards
                confirmParams: {
                    return_url: window.location.href,
                },
                redirect: "if_required",
            });

            if (error) {
                setErrorMessage(error.message || 'Payment failed');
            } else if (paymentIntent && paymentIntent.status === 'succeeded') {
                onSuccess(paymentIntent.id);
            } else {
                setErrorMessage(`Payment status: ${paymentIntent?.status}`);
            }
        } catch (err: any) {
            setErrorMessage(err.message || 'An unexpected error occurred.');
        } finally {
            setProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="stripe-payment-form">
            <div className="stripe-element-container">
                <PaymentElement />
            </div>
            {errorMessage && <div className="stripe-error-message">{errorMessage}</div>}
            <button type="submit" disabled={!stripe || processing} className="stripe-submit-btn">
                {processing ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
            </button>
        </form>
    );
};

interface StripePaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    amount: number;
    onSuccess: (paymentId: string) => void;
}

const StripePaymentModal: React.FC<StripePaymentModalProps> = ({ isOpen, onClose, amount, onSuccess }) => {
    const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [pk, setPk] = useState(process.env.REACT_APP_STRIPE_PUBLIC_KEY || ''); // Store PK input
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleCreateIntent = useCallback(async () => {
        if (!pk.trim().startsWith('pk_')) {
            setError('Please enter a valid Publishable Key (pk_...)');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const data = await createPaymentIntent(Math.round(amount * 100));

            if (data.clientSecret) {
                setClientSecret(data.clientSecret);
                // Initialize Stripe
                setStripePromise(loadStripe(pk.trim()));
            } else {
                setError('Failed to get clientSecret from server.');
            }
        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Error creating PaymentIntent');
        } finally {
            setLoading(false);
        }
    }, [amount, pk]); // Added dependencies for useCallback

    // Auto-init if PK is in env
    useEffect(() => {
        if (isOpen && !clientSecret && !loading && pk.startsWith('pk_')) {
            handleCreateIntent();
        }
    }, [isOpen, clientSecret, loading, pk, handleCreateIntent]); // Added handleCreateIntent to dependencies

    if (!isOpen) return null;


    return (
        <div className="stripe-modal-overlay">
            <div className="stripe-modal-content">
                <div className="stripe-modal-header">
                    <h2 className="stripe-modal-title">Secure Payment</h2>
                    <button className="stripe-modal-close" onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {!clientSecret ? (
                    <div className="stripe-setup-step">
                        {/* Only show input if NOT from env or if we want to allow override in error case? 
                            For now, if empty process.env, show input. */}
                        {!process.env.REACT_APP_STRIPE_PUBLIC_KEY && (
                            <div className="pk-input-container">
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'rgba(213, 190, 155, 0.8)' }}>
                                    Stripe Publishable Key:
                                </label>
                                <input
                                    className="pk-input"
                                    value={pk}
                                    onChange={(e) => setPk(e.target.value)}
                                    placeholder="pk_test_..."
                                />
                            </div>
                        )}

                        <p style={{ marginBottom: '1rem', color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                            Amount to pay: <strong>${amount.toFixed(2)}</strong>
                        </p>

                        {error && <div className="stripe-error-message" style={{ marginBottom: '1rem' }}>{error}</div>}

                        {/* If auto-loading from env, show status. Else show button */}
                        {(process.env.REACT_APP_STRIPE_PUBLIC_KEY && loading) ? (
                            <div style={{ textAlign: 'center', color: '#fff' }}>Initializing Payment...</div>
                        ) : (
                            !process.env.REACT_APP_STRIPE_PUBLIC_KEY && (
                                <button
                                    className="stripe-submit-btn"
                                    onClick={handleCreateIntent}
                                    disabled={loading}
                                >
                                    {loading ? 'Initializing...' : 'Proceed to Payment'}
                                </button>
                            )
                        )}
                    </div>
                ) : (
                    stripePromise && clientSecret && (
                        <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night', variables: { colorPrimary: '#d5be9b' } } }}>
                            <CheckoutForm
                                amount={amount}
                                onSuccess={onSuccess}
                                onClose={onClose}
                            />
                        </Elements>
                    )
                )}
            </div>
        </div>
    );
};

export default StripePaymentModal;
